<template>
    <div :id="widgetId"
         :style="captchaStyles"
         class="smart-captcha" />
</template>

<script>
export default {
    name: 'YandexCaptcha',

    props: {
        /**
         * 	Ключ клиентской части.
         */
        siteKey: {
            type    : String,
            required: true,
        },

        /**
         * 	Язык виджета. Возможные значения: 'ru', 'en', 'be', 'kk', 'tt', 'uk', 'uz', 'tr'.
         */
        language: {
            type   : String,
            default: 'ru',
            validator(lang) {
                const supportedLanguages = ['ru', 'en', 'be', 'kk', 'tt', 'uk', 'uz', 'tr'];
                return supportedLanguages.includes(lang);
            },
        },

        /**
         *  Включение работы капчи в режиме тестирования.
         *  Пользователь всегда будет получать задание.
         *  Используйте только для отладки и тестирования.
         */
        test: {
            type   : Boolean,
            default: false,
        },

        /**
         * 	Запуск капчи в WebView для повышения точности оценки пользователей в мобильных приложениях.
         */
        webview: {
            type   : Boolean,
            default: false,
        },

        /**
         * 	Невидимая капча.
         */
        invisible: {
            type   : Boolean,
            default: false,
        },

        /**
         * 	Расположение блока с уведомлением об обработке данных.
         */
        shieldPosition: {
            type   : String,
            default: 'bottom-right',
        },

        /**
         * 	Скрыть блок с уведомлением об обработке данных.
         */
        hideShield: {
            type   : Boolean,
            default: false,
        },

        /**
         * 	Загружать виджет при монтировании компонента.
         */
        loadWidget: {
            type   : Boolean,
            default: true,
        },
    },

    data() {
        return {
            widgetId: 'yandex-captcha',
            script  : null
        };
    },

    computed: {
        captchaStyles() {
            return this.invisible ? { height: '0', width: '0', margin: 0, padding: 0 } : { width: 'auto' };
        },
    },

    mounted() {
        if (!this.loadWidget) return;

        this.script = Array.from(document.getElementsByTagName('script')).find(s => s.src.includes('smartcaptcha.yandexcloud.net/captcha.js'));

        if (this.script) {
            this.script.parentNode.removeChild(this.script);
        }

        this.script = document.createElement('script');
        this.script.src = 'https://smartcaptcha.yandexcloud.net/captcha.js?render=onload&onload=onloadFunction';
        this.script.type = 'text/javascript';
        this.script.defer = true;
        this.script.crossOrigin = 'anonymous';
        document.head.appendChild(this.script);

        this.script.onload = () => {
            this.initializeCaptcha();
        };

        this.script.onerror = () => {
            this.$emit('onJavaScriptError', new Error('SmartCaptcha script failed'));
        };

        this.$emit('passExecuteMethod', this.execute.bind(this));
    },

    beforeDestroy() {
        if (window && window.smartCaptcha) {
            try {
                window.smartCaptcha.destroy(this.widgetId);
            } catch (error) {}
        }

        this.$emit('passExecuteMethod', null);

        if (document && document.getElementsByClassName('smart-captcha').length === 1) {
            if (this.script) {
                this.script.parentNode.removeChild(this.script);
            }
        }
    },

    methods: {
        initializeCaptcha() {
            if (window && document && window.smartCaptcha && this.widgetId) {
                const container = document.getElementById(this.widgetId);

                if (!container || !window.smartCaptcha) {
                    throw new Error('SmartCaptcha is not ready');
                }
                

                const widgetId = window.smartCaptcha.render(container, {
                    sitekey       : this.siteKey,
                    hl            : this.language,
                    test          : this.test,
                    webview       : this.webview,
                    invisible     : this.invisible,
                    shieldPosition: this.shieldPosition,
                    hideShield    : this.hideShield,
                    callback      : this.callback,
                });

                if (widgetId === undefined || widgetId === null) {
                    this.$emit('onJavaScriptError', new Error('SmartCaptcha render failed'));
                    return;
                }

                this.widgetId = widgetId;

                window.smartCaptcha.subscribe(this.widgetId, 'success', this.success);
                window.smartCaptcha.subscribe(this.widgetId, 'challenge-visible', this.challengeVisible);
                window.smartCaptcha.subscribe(this.widgetId, 'challenge-hidden', this.challengeHidden);
                window.smartCaptcha.subscribe(this.widgetId, 'network-error', this.netError);
                window.smartCaptcha.subscribe(this.widgetId, 'javascript-error', this.jsError);
                window.smartCaptcha.subscribe(this.widgetId, 'token-expired', this.tokenExpired);
            }
        },

        /**
         * 	Функция-обработчик, возвращает токен в случае успеха
         *  @param token {string}
         */
        callback(token) {
            this.$emit('callback', token);
        },

        /**
         * 	Открытие всплывающего окна с заданием
         */
        challengeVisible() {
            this.$emit('onChallengeVisible');
        },

        /**
         * 	Закрытие всплывающего окна с заданием
         */
        challengeHidden() {
            this.$emit('onChallengeHidden');
        },

        /**
         * 	Возникла сетевая ошибка
         */
        netError() {
            this.$emit('onNetworkError', { message: 'Network error occured' });
        },

        /**
         * 	Возникла критическая ошибка JS
         *  @param error {object}
         */
        jsError(error) {
            this.$emit('onJavaScriptError', error);
        },

        /**
         * 	Успешная валидация пользователя
         *  @param token {string}
         */
        success(token) {
            this.$emit('onSuccess', token);
        },

        /**
         * 	Токен прохождения проверки стал невалидным
         */
        tokenExpired() {
            this.$emit('onTokenExpired', null);
        },

        /**
         * 	Запускает проверку пользователя. Используется для запуска невидимой капчи.
         */
        execute() {
            if (!window.smartCaptcha) {
                this.$emit('onJavaScriptError', new Error('SmartCaptcha is not ready'));
                return;
            }

            try {
                window.smartCaptcha.execute(this.widgetId);
            } catch (error) {
                this.$emit('onJavaScriptError', error);
            }
        },

        /**
         * 	Возвращает текущее значение токена пользователя.
         */
        getResponse() {
            if (window.smartCaptcha) {
                return window.smartCaptcha.getResponse(this.widgetId);
            }

            return '';
        },

        /**
         * 	Сбрасывает состояние виджета до начального.
         */
        reset() {
            if (window.smartCaptcha) {
                window.smartCaptcha.reset(this.widgetId);
            }
        },

        /**
         * 	Удаляет виджет и созданные им обработчики.
         */
        destroy() {
            if (window.smartCaptcha) {
                window.smartCaptcha.destroy(this.widgetId);
            }
        },

        /**
         * 	Подписывает обработчики на определенные события виджета.
         * 	Например, для отслеживания открытия и закрытия всплывающего окна с заданием.
         *  @param eventName {Event}
         *  @param callbackFun {Function}
         */
        subscribe(eventName, callbackFun) {
            if (window.smartCaptcha) {
                window.smartCaptcha.subscribe(this.widgetId, eventName, callbackFun);
            }
        },
    },
};
</script>

